import { useEffect, useState } from 'react'
import styled from 'styled-components'
import Modal from '../../../common/Modal'
import useReadingSessionStore from './useReadingSessionStore'
import { Button as BootstrapButton, Form } from 'react-bootstrap'
import RowSpacer from '../../../common/styles/RowSpacer'
import { BooksitoutServer } from '../../../config/BooksitoutServer'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import NumberInput from '../../../common/form/NumberInput'
import useBookDetailStore from '../detail/useBookDetailStore'
import useBookDetail from '../detail/useBookDetail'
import BookReadingSessionResponse from './BookReadingSessionResponse'
import BottomCTA from '../../../common/button/BottomCTA'
import booksitoutIcon from '../../../config/BooksitoutIcon'

const BookReadingSessionEndModal = () => {
	const { isEndModalOpen, closeEndModal } = useReadingSessionStore()

	return <Modal titleText={'독서활동 끝내기'} isShowing={isEndModalOpen} onClose={closeEndModal} body={<Body />} size={'md'} />
}

const Body = () => {
	const navigate = useNavigate()

	const { currentReadingSession, stop, bookId, readingSessionId, timerInSeconds, updateCurrentReadingSession, playConfetti } = useReadingSessionStore()

	const { updateBookStatus } = useBookDetailStore()

	// @ts-ignore
	const [startPage, setStartPage] = useState<string | null>(currentReadingSession?.book?.currentPage != null ? String(currentReadingSession.book.currentPage) : null)
	const [endPage, setEndPage] = useState<string | null>()
	const [percent, setPercent] = useState<boolean>(false)
	const [startPercent, setStartPercent] = useState<string | null>()
	const [endPercent, setEndPercent] = useState<string | null>()

	useEffect(() => {
		setStartPage(currentReadingSession?.book?.currentPage != null ? String(currentReadingSession.book.currentPage) : null)
		setStartPercent(currentReadingSession?.book?.currentPage != null ? String(Math.ceil((currentReadingSession.book.currentPage / currentReadingSession?.book?.endPage) * 100)) : null)
        setPercent(currentReadingSession?.readingSession?.method == 'PERCENTAGE')
	}, [currentReadingSession])

	const [bookDetail] = useBookDetail(Number(bookId))
	const [readingSessions, setReadingSessions] = useState<BookReadingSessionResponse[]>([])
	useEffect(() => {
		setReadingSessions(bookDetail?.readingSessions ?? [])
	}, [bookDetail])

	const { addReadingSession } = useBookDetailStore()

	// 페이지 추측
	const [isPredictButtonDisabled, setIsPredictButtonDisabled] = useState<boolean>(true)
	useEffect(() => {
		if (startPage != null && timerInSeconds != null && timerInSeconds > 60 && (endPage == null || endPage == '') && readingSessions.length > 0) {
			setIsPredictButtonDisabled(false)
		} else {
			setIsPredictButtonDisabled(true)
		}
	}, [startPage, endPage, timerInSeconds])

	const predictEndPage = () => {
		const readTimeInMinutes = timerInSeconds / 60

		if (!(startPage != null && timerInSeconds != null && timerInSeconds > 60 && (endPage == null || endPage == ''))) {
			toast.error(`정보가 충분하지 않아 페이지를 추측할 수 없었어요.`)
			return
		}

		const totalPagesRead = readingSessions
			.filter(readingSession => readingSession.startPage != null && readingSession.endPage != null)
			.reduce((acc, readingSession) => {
				return acc + (readingSession.endPage!! - readingSession.startPage!! + 1)
			}, 0)

		const totalTimeRead = readingSessions.filter(readingSession => readingSession.startPage != null && readingSession.endPage != null).reduce((acc, readingSession) => acc + readingSession.readTimeInMinutes, 0)

		const averagePagePerMinute = totalPagesRead / totalTimeRead

		const predictedPages = String(Number(startPage) + Math.floor(Number(readTimeInMinutes) * averagePagePerMinute) - 1);
		const bookEndPages = bookDetail?.book?.endPage

		setEndPage(bookEndPages ? Math.min(predictedPages, bookEndPages) : predictedPages)

		toast.success(`그 전 독서활동을 바탕으로 페이지를 추측했어요`)
	}

	const totalPage = Number(currentReadingSession?.book?.endPage) || 0
	const startPercentToPage = totalPage ? Math.floor((Number(startPercent) / 100) * totalPage) : 0

	const endPercentToPage = totalPage ? Math.floor((Number(endPercent) / 100) * totalPage) : 0

	const handleSave = e => {
		e?.preventDefault()

		const bookEndPage = currentReadingSession?.book?.endPage

        let startPageComputed = percent ?
            (startPercent != null ? startPercentToPage : null) :
            (endPage == null ? null : startPage != null ? startPage : null);

        let endPageComputed = percent ?
            (endPercent != null ? endPercentToPage : null) :
            (endPage != null ? endPage : null);

        if (startPageComputed != null && Number(startPageComputed) < 0) {
			toast.error('시작 페이지는 0 이상이어야 해요.')
			return
		}

		if (endPageComputed != null && Number(endPageComputed) < 0) {
			toast.error('끝 페이지는 0 이상이어야 해요.')
			return
		}

		if (startPageComputed != null && endPageComputed != null && Number(endPageComputed) < Number(startPageComputed)) {
			toast.error('끝 페이지는 시작 페이지보다 커야 해요.')
			return
		}

		if (endPageComputed != null && bookEndPage != null && Number(endPageComputed) > bookEndPage) {
			toast.error('끝 페이지는 책의 마지막 페이지보다 작아야 해요.')
			return
		}

		let isLast = false
		if (endPageComputed != null && bookEndPage != null && Number(endPageComputed) >= bookEndPage) {
			isLast = window.confirm('책을 다 읽은 거 같아요. 완료 처리할까요?')
		}

        const body = {
			endTime: new Date(),
            readingTimeInSeconds: timerInSeconds,
			startPage: startPageComputed,
			endPage: endPageComputed,
            method: percent ? 'PERCENTAGE' : 'PAGE',
			isLast: isLast,
		}

		BooksitoutServer.post(`/v1/book/reading-session/${readingSessionId}/end`, body)
			.then(res => {
				stop()
				addReadingSession(res.data)
				updateCurrentReadingSession(null)

				if (isLast) {
					playConfetti()
					updateBookStatus('DONE')
					toast.success('책을 다 읽었어요! 축하 드려요! 👏')
				} else {
					toast.success('독서활동을 저장했어요')
				}

				navigate(`/book/mine/${bookId}`)
			})
			.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요'))
	}

	const handleDelete = () => {
		BooksitoutServer.delete(`/v1/book/reading-session/end`)
			.then(() => {
				toast.success('독서활동을 삭제했어요.')
				stop()
				updateCurrentReadingSession(null)
				navigate(`/book/mine/${bookId}`)
			})
			.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요'))
	}

	const handleChangeToPercent = () => {
		setPercent(true)
	}

	return (
		<Form onSubmit={handleSave}>
			<Row className="justify-content-end">
				<div className="col-auto">
					<button type="button" className="border-0 bg-transparent text-book">
						{percent ? (
							<div onClick={() => setPercent(false)}>
								<booksitoutIcon.page style={{ fontSize: '26px' }} />
							</div>
						) : (
							<div onClick={handleChangeToPercent}>
								<booksitoutIcon.percent style={{ fontSize: '26px' }} />
							</div>
						)}
					</button>
				</div>
			</Row>
			<Row>
				{percent ? (
					<>
						<Col>
							<h6>시작 퍼센트</h6>
							<div className="d-flex">
								<NumberInput placeholder={'시작 퍼센트'} onChange={e => setStartPercent(e.target.value)} isFocusOnAppear={false} value={startPercent} min={0} max={100} />
								<span className="text-book" style={{ marginLeft: '-65px', fontSize: '14px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
									{Number.isFinite(startPercentToPage) ? startPercentToPage : 0}페이지
								</span>
							</div>
						</Col>

						<Col>
							<h6>끝 퍼센트</h6>
							<div className="d-flex">
								<NumberInput placeholder={'끝 퍼센트'} isFocusOnAppear={true} min={startPercent ? Number(startPercent) : 0} max={100} value={endPercent} onChange={e => setEndPercent(e.target.value)} minErrorMessage={'끝 퍼센트는 시작 퍼센트보다 커야 해요.'} />
								<span className="text-book" style={{ marginLeft: '-65px', fontSize: '14px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
									{Number.isFinite(endPercentToPage) ? endPercentToPage : 0}페이지
								</span>
							</div>
						</Col>
					</>
				) : (
					<>
						<Col>
							<h6>시작 페이지</h6>
							<NumberInput placeholder={'시작 페이지'} onChange={e => setStartPage(e.target.value)} isFocusOnAppear={false} value={startPage} min={0} max={currentReadingSession?.book?.endPage} />
						</Col>

						<Col>
							<h6>끝 페이지</h6>
							<NumberInput placeholder={'끝 페이지'} isFocusOnAppear={true} min={startPage ? Number(startPage) : 0} max={currentReadingSession?.book?.endPage} value={endPage} onChange={e => setEndPage(e.target.value)} minErrorMessage={'끝 페이지는 시작 페이지보다 커야 해요.'} />
						</Col>
					</>
				)}
			</Row>

			<RowSpacer size={40} />

			<div className={'d-flex justify-content-end'}>
				<BootstrapButton
					disabled={isPredictButtonDisabled}
					style={{ backgroundColor: 'var(--secondary-btn-bg)', border: 'none', color: 'var(--text-color)', borderRadius: '16px', fontWeight: '600' }}
					onClick={predictEndPage}
				>
					읽은 시간으로 추측하기
				</BootstrapButton>
			</div>

			<RowSpacer size={5} />

			<Row>
				<ColDelete>
					{/* @ts-ignore */}
					<Button variant={'book-danger'} onClick={handleDelete}>
						삭제하기
					</Button>
				</ColDelete>
			</Row>

			<BottomCTA
				message={percent
					? (endPercent == null || endPercent == '' ? '퍼센트 없이 저장하기' : '저장하기')
					: (endPage == null || endPage == '' ? '페이지 없이 저장하기' : '저장하기')}
				onClick={() => handleSave(null)}
			/>
		</Form>
	)
}

const Row = styled.div.attrs({
	className: 'row mt-2 mt-md-0',
})`
	justify-content: flex-end;
`

const Col = styled.div.attrs({
	className: 'col-12 col-md-6 mt-2',
})``

const ColDelete = styled.div.attrs({
	className: 'col-4 col-md-4 mt-2 mt-md-0',
})``

const Button = styled(BootstrapButton).attrs({})`
	width: 100%;
`

export default BookReadingSessionEndModal
